// /login ページ — メールアドレス + パスワードでログイン

import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { ApiError } from "../api/client";

const isMobileUA = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// 招待制サインアップの feature flag。false なら登録リンクも URL も完全に露出しない。
const ENABLE_SIGNUP = import.meta.env.VITE_ENABLE_SIGNUP === "true";

type State =
  | { phase: "idle"; error?: string }
  | { phase: "submitting" }
  | { phase: "challenge"; challengeId: string; maskedEmail: string; expiresIn: number }
  | { phase: "error"; error: string };

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<State>({ phase: "idle" });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setState({ phase: "submitting" });
    try {
      // 2026-08-17〜:email + password のみ。AuthProvider.login は第 3 引数を
      // 受け取っても無視するので、ここでは渡さない。
      const resp = await login(email, password);
      if (resp.status === "authenticated") {
        const from = (location.state as { from?: string } | null)?.from;
        const target = from
          ? from
          : isMobileUA()
          ? "/mobile/dashboard"
          : "/dashboard";
        navigate(target, { replace: true });
      } else {
        // challenge_required
        setState({
          phase: "challenge",
          challengeId: resp.challenge_id,
          maskedEmail: resp.masked_destination,
          expiresIn: resp.expires_in_seconds,
        });
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "ログインに失敗しました";
      const code = e instanceof ApiError ? e.code : "UNKNOWN";
      // 423 のときは明示的に文言差し替え
      if (code === "AUTH_ACCOUNT_LOCKED") {
        setState({ phase: "error", error: "アカウントは一時的にロックされています。30 分後に再試行してください。" });
      } else {
        setState({ phase: "error", error: msg });
      }
    }
  }

  if (state.phase === "challenge") {
    // verify ページに遷移(state に challenge_id を載せる)
    navigate("/login/verify", {
      replace: true,
      state: {
        challenge_id: state.challengeId,
        masked_destination: state.maskedEmail,
        expires_in_seconds: state.expiresIn,
      },
    });
    return null;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo" aria-hidden="true">
          <img src="/nexus-logo.png" alt="" />
        </div>
        <h1 className="login-title">NEXUS WP統合管理ポータル</h1>
        <p className="login-subtitle">WordPress 連携運営管理システム</p>

        <form className="login-form" onSubmit={onSubmit}>
          <div className="login-form-row">
            <label className="login-label">メールアドレス</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              disabled={state.phase === "submitting"}
            />
          </div>

          <div className="login-form-row">
            <label className="login-label">パスワード</label>
            <input
              className="login-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={state.phase === "submitting"}
            />
          </div>

          {state.phase === "error" && (
            <div className="login-error">{state.error}</div>
          )}

          <button
            className="login-submit"
            type="submit"
            disabled={state.phase === "submitting"}
          >
            {state.phase === "submitting" ? "ログイン中..." : "ログイン"}
          </button>
        </form>

        {ENABLE_SIGNUP && (
          <div className="login-signup-row">
            アカウントをお持ちでない方は{" "}
            <Link to="/register" className="login-signup-link">
              新規登録
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
